import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_OPEN_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { cn } from '@/lib/utils'

interface CategoryOption {
  id: string
  name: string
}

interface Props {
  value: string[]
  onChange: (value: string[]) => void
  categories: CategoryOption[]
  className?: string
}

// Category filter — select-field trigger with a searchable panel, matching
// TechnicianCityFilter (TechniciansListPage City option).
export function ServiceCategoryFilter({
  value,
  onChange,
  categories,
  className,
}: Props) {
  const { t } = useTranslation('settings')
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const openRef = useRef(open)

  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!openRef.current) return
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (!openRef.current) return
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const trimmedTerm = term.trim()

  const filtered = useMemo(() => {
    const q = trimmedTerm.toLowerCase()
    if (!q) return categories
    return categories.filter(c => c.name.toLowerCase().includes(q))
  }, [categories, trimmedTerm])

  const triggerLabel =
    value.length === 0
      ? t('items.filters.category')
      : t('items.filters.selectedCategories', { count: value.length })

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id])

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type='button'
        aria-expanded={open}
        onClick={() => {
          if (!open) setTerm('')
          setOpen(o => !o)
        }}
        className={cn(
          'inline-flex h-10 min-w-[120px] shrink-0 items-center justify-between gap-2 rounded-xl border border-[#cfd4dc] bg-white py-3 pl-4 pr-3 text-sm font-normal text-[#171a20]',
          FORM_CONTROL_FOCUS_CLASS,
          FORM_FILTER_HOVER_CLASS,
          open && FORM_CONTROL_OPEN_CLASS
        )}
      >
        <span className='truncate'>{triggerLabel}</span>
        <ChevronDown className='h-4 w-4 shrink-0 opacity-60' />
      </button>

      {open && (
        <div className='absolute left-0 z-50 mt-1 w-[240px] rounded-lg border border-border bg-popover p-2 text-popover-foreground shadow-md'>
          <input
            autoFocus
            value={term}
            onChange={e => setTerm(e.target.value)}
            placeholder={t('items.filters.categorySearchPlaceholder')}
            className='mb-2 h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm font-normal outline-none focus-visible:ring-2 focus-visible:ring-ring'
          />

          {value.length > 0 && (
            <button
              type='button'
              onClick={() => onChange([])}
              className='mb-1 w-full rounded px-2 py-1 text-left text-xs text-primary hover:bg-accent'
            >
              {t('items.filters.clearCategory')}
            </button>
          )}

          <div className='max-h-48 overflow-y-auto'>
            {filtered.map(cat => (
              <label
                key={cat.id}
                className='flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm font-normal hover:bg-accent'
              >
                <Checkbox
                  checked={value.includes(cat.id)}
                  onCheckedChange={() => toggle(cat.id)}
                />
                <span className='truncate'>{cat.name}</span>
              </label>
            ))}

            {filtered.length === 0 && (
              <div className='px-2 py-2 text-sm text-muted-foreground'>
                {t('items.filters.noCategoryOptions')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
